const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 250;

/**
 * `next/dynamic` never retries, so one dropped chunk request is permanent — and a
 * chunk that fails while it is being rendered throws where there is nothing left
 * to show instead, which is what a publisher reports as a player who arrived and
 * then did nothing.
 *
 * Every remaining caller is on that footing. The shared-ui loader is what the
 * player is shown *while* they wait, so its own request failing leaves the wait
 * with no surface at all; the cookie gate silently drops consent for the visit.
 * Anything needed on every page view has since been imported directly instead,
 * because splitting it off never saved a download and only added a request that
 * could fail.
 *
 * Retrying is enough to recover because webpack drops the chunk from its registry
 * before rejecting (`installedChunks[chunkId] = undefined` on the error path in
 * JsonpChunkLoadingRuntimeModule), so calling the loader again issues a fresh
 * request rather than handing back the same rejection.
 */
export const retryChunkImport = <T>(load: () => Promise<T>): Promise<T> =>
  attemptChunkImport(load, 1);

const attemptChunkImport = <T>(load: () => Promise<T>, attempt: number): Promise<T> =>
  load().catch((error: unknown) => {
    if (attempt >= MAX_ATTEMPTS || !isChunkLoadError(error)) {
      throw error;
    }

    // Spacing the attempts out rides over a brief loss of connectivity, which is
    // the usual cause on the mobile networks these players arrive on.
    return wait(RETRY_BASE_DELAY_MS * attempt).then(() =>
      attemptChunkImport(load, attempt + 1)
    );
  });

/** Anything else is a real fault, and repeating it only delays the report. */
const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' || /Loading chunk .+ failed/.test(error.message));

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
